import {Grid} from '@material-ui/core';
import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {ConditionalSection, QuoteFormConditionalSection} from 'Quoting/components/shared/QuoteFormConditionalSection';
import React from 'react';
import {useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {FormRowGrid, GridRowProps} from '../../../../shared/GridRow';
import {QuoteFormControlElement} from '../../../../shared/QuoteFormControlElement';
import {QuoteFormElement} from '../../../../shared/QuoteFormElement';
import {FormWrapper} from '../../../../shared/QuoteFormWrapper';
import {unitsOptions} from '../../PlanTemplates/ACC/benefitDefaults';
import {formatCurrencyValue} from './ACCBenefits';
import {accidentAmbulanceCarePath} from './PropertyPaths/propertyPathACC';

// Urgent Care Component
const UrgentCare = () => {
  const {getQuoteFieldValue} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);

  const propertyPaths = accidentAmbulanceCarePath(selectedPlan?.planIndex || 0, 0);

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'urgent-care-row-1'},
      items: [
        {
          key: propertyPaths.urgentCareDetails.incurralPeriod,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Incurral Period (Days)"
              value={getQuoteFieldValue<number>(propertyPaths.urgentCareDetails.incurralPeriod)}
              disabled
              required
              id={propertyPaths.urgentCareDetails.incurralPeriod}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.urgentCareDetails.customizeBenefitAmount,
          hidden: true,
          element: (
            <QuoteFormControlElement
              controlType="checkbox"
              label="Customize Benefit Amount"
              disabled
              checked={getQuoteFieldValue<boolean>(propertyPaths.urgentCareDetails.customizeBenefitAmount)}
              id={propertyPaths.urgentCareDetails.customizeBenefitAmount}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'urgent-care-row-2'},
      items: [
        {
          key: propertyPaths.urgentCareDetails.benefitAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount"
              adornment="money"
              value={formatCurrencyValue(getQuoteFieldValue<number>(propertyPaths.urgentCareDetails.benefitAmount))}
              disabled
              required
              id={propertyPaths.urgentCareDetails.benefitAmount}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.urgentCareDetails.maxBenefitNumber,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`ACCAmbulanceUrgentCareBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(propertyPaths.urgentCareDetails.maxBenefitNumber)}
              disabled
              required
              id={propertyPaths.urgentCareDetails.maxBenefitNumber}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'urgent-care-row-3'},
      items: [
        {
          key: propertyPaths.urgentCareDetails.timePeriodCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`ACCAmbulanceUrgentCareBenefit.timePeriodCd`}
              label="Time Period"
              value={getQuoteFieldValue<number>(propertyPaths.urgentCareDetails.timePeriodCd)}
              disabled
              required
              id={propertyPaths.urgentCareDetails.timePeriodCd}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

// Accident Physician Treatment Component
const AccidentPhysicianTreatment = () => {
  const {getQuoteFieldValue} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);

  const propertyPaths = accidentAmbulanceCarePath(selectedPlan?.planIndex || 0, 0);

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'accident-physician-treatment-row-1'},
      items: [
        {
          key: propertyPaths.ambulanceUrgentCareBenefit.incurralPeriod,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Incurral Period (Days)"
              value={getQuoteFieldValue<number>(propertyPaths.ambulanceUrgentCareBenefit.incurralPeriod)}
              disabled
              required
              id={propertyPaths.ambulanceUrgentCareBenefit.incurralPeriod}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.ambulanceUrgentCareBenefit.customizeBenefitAmount,
          hidden: true,
          element: (
            <QuoteFormControlElement
              controlType="checkbox"
              label="Customize Benefit Amount"
              checked={getQuoteFieldValue<boolean>(propertyPaths.ambulanceUrgentCareBenefit.customizeBenefitAmount)}
              id={propertyPaths.ambulanceUrgentCareBenefit.customizeBenefitAmount}
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'accident-physician-treatment-row-2'},
      items: [
        {
          key: propertyPaths.ambulanceUrgentCareBenefit.benefitAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount"
              adornment="money"
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.ambulanceUrgentCareBenefit.benefitAmount)
              )}
              disabled
              required
              id={propertyPaths.ambulanceUrgentCareBenefit.benefitAmount}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.ambulanceUrgentCareBenefit.maxBenefitNumber,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`ACCAmbulanceUrgentCareBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(propertyPaths.ambulanceUrgentCareBenefit.maxBenefitNumber)}
              disabled
              required
              id={propertyPaths.ambulanceUrgentCareBenefit.maxBenefitNumber}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'accident-physician-treatment-row-3'},
      items: [
        {
          key: propertyPaths.ambulanceUrgentCareBenefit.timePeriodCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Time Period"
              searchKey={`ACCAmbulanceUrgentCareBenefit.timePeriodCd`}
              value={getQuoteFieldValue<number>(propertyPaths.ambulanceUrgentCareBenefit.timePeriodCd)}
              disabled
              required
              id={propertyPaths.ambulanceUrgentCareBenefit.timePeriodCd}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

// Air Ambulance Component
const AirAmbulance = () => {
  const {getQuoteFieldValue} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);

  const propertyPaths = accidentAmbulanceCarePath(selectedPlan?.planIndex || 0, 0);

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'air-ambulance-row-1'},
      items: [
        {
          key: propertyPaths.ambulanceAirDetails.incurralPeriod,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Incurral Period (Days)"
              value={getQuoteFieldValue<number>(propertyPaths.ambulanceAirDetails.incurralPeriod)}
              disabled
              required
              id={propertyPaths.ambulanceAirDetails.incurralPeriod}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.ambulanceAirDetails.customizeBenefitAmount,
          hidden: true,
          element: (
            <QuoteFormControlElement
              controlType="checkbox"
              label="Customize Benefit Amount"
              checked={getQuoteFieldValue<boolean>(propertyPaths.ambulanceAirDetails.customizeBenefitAmount)}
              id={propertyPaths.ambulanceAirDetails.customizeBenefitAmount}
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'air-ambulance-row-2'},
      items: [
        {
          key: propertyPaths.ambulanceAirDetails.benefitAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount"
              value={formatCurrencyValue(getQuoteFieldValue<number>(propertyPaths.ambulanceAirDetails.benefitAmount))}
              disabled
              adornment="money"
              required
              id={propertyPaths.ambulanceAirDetails.benefitAmount}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.ambulanceAirDetails.maxBenefitNumber,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`ACCAmbulanceAirBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(propertyPaths.ambulanceAirDetails.maxBenefitNumber)}
              disabled
              required
              id={propertyPaths.ambulanceAirDetails.maxBenefitNumber}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'air-ambulance-row-3'},
      items: [
        {
          key: propertyPaths.ambulanceAirDetails.timePeriodCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`ACCAmbulanceAirBenefit.timePeriodCd`}
              label="Time Period"
              value={getQuoteFieldValue<number>(propertyPaths.ambulanceAirDetails.timePeriodCd)}
              disabled
              required
              id={propertyPaths.ambulanceAirDetails.timePeriodCd}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

// Ground Ambulance Component
const GroundAmbulance = () => {
  const {getQuoteFieldValue} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);

  const propertyPaths = accidentAmbulanceCarePath(selectedPlan?.planIndex || 0, 0);

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'ground-ambulance-row-1'},
      items: [
        {
          key: propertyPaths.ambulanceGroundOrWaterDetails.incurralPeriod,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Incurral Period (Days)"
              value={getQuoteFieldValue<number>(propertyPaths.ambulanceGroundOrWaterDetails.incurralPeriod)}
              disabled
              required
              id={propertyPaths.ambulanceGroundOrWaterDetails.incurralPeriod}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.ambulanceGroundOrWaterDetails.customizeBenefitAmount,
          hidden: true,
          element: (
            <QuoteFormControlElement
              controlType="checkbox"
              label="Customize Benefit Amount"
              checked={getQuoteFieldValue<boolean>(propertyPaths.ambulanceGroundOrWaterDetails.customizeBenefitAmount)}
              id={propertyPaths.ambulanceGroundOrWaterDetails.customizeBenefitAmount}
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'ground-ambulance-row-2'},
      items: [
        {
          key: propertyPaths.ambulanceGroundOrWaterDetails.benefitAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount"
              adornment="money"
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.ambulanceGroundOrWaterDetails.benefitAmount)
              )}
              disabled
              required
              id={propertyPaths.ambulanceGroundOrWaterDetails.benefitAmount}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.ambulanceGroundOrWaterDetails.maxBenefitNumber,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`ACCAmbulanceGroundOrWaterBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(propertyPaths.ambulanceGroundOrWaterDetails.maxBenefitNumber)}
              disabled
              required
              id={propertyPaths.ambulanceGroundOrWaterDetails.maxBenefitNumber}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'ground-ambulance-row-3'},
      items: [
        {
          key: propertyPaths.ambulanceGroundOrWaterDetails.timePeriodCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`ACCAmbulanceGroundOrWaterBenefit.timePeriodCd`}
              label="Time Period"
              value={getQuoteFieldValue<number>(propertyPaths.ambulanceGroundOrWaterDetails.timePeriodCd)}
              disabled
              required
              id={propertyPaths.ambulanceGroundOrWaterDetails.timePeriodCd}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

export const AccidentAmbulanceUrgentCareBlock = () => {
  const {getQuoteFieldValue} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);

  const propertyPaths = accidentAmbulanceCarePath(selectedPlan?.planIndex || 0, 0);

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'accident-urgent-care-units-row'},
      items: [
        {
          key: 'units',
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Units"
              options={unitsOptions}
              type="number"
              required
              disabled
              value={getQuoteFieldValue<number>(propertyPaths.unitsOfAccAmbulanceUrgentCare)}
              id={propertyPaths.unitsOfAccAmbulanceUrgentCare}
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
      ],
    },
  ];

  const accidentUrgentCareSections: ConditionalSection[] = [
    {
      Component: AccidentPhysicianTreatment,
      key: 'AccidentPhysicianTreatment',
      label: 'ACCIDENT PHYSICIAN TREATMENT',
      disabled: true,
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isAccidentPhysicianTreatmentApplied),
    },
    {
      Component: AirAmbulance,
      key: 'AirAmbulance',
      label: 'AIR AMBULANCE',
      disabled: true,
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isAmbulanceAirApplied),
    },
    {
      Component: GroundAmbulance,
      key: 'GroundAmbulance',
      label: 'GROUND AMBULANCE',
      disabled: true,
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isAmbulanceGroundOrWaterApplied),
    },
    {
      Component: UrgentCare,
      key: 'UrgentCare',
      label: 'URGENT CARE',
      disabled: true,
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isUrgentCareApplied),
    },
  ];

  return (
    <FormWrapper title="Accident Treatment and Urgent Care - Ambulance Urgent Care" collapsible withSeparator>
      <Grid container spacing={3}>
        <FormRowGrid rows={rows} />
        <QuoteFormConditionalSection sections={accidentUrgentCareSections} />
      </Grid>
    </FormWrapper>
  );
};
